import { useEffect } from "react";
import { RoomEvent } from "livekit-client";
import useVideoContext from "../useVideoContext/useVideoContext";
import { logLiveKit, LogLevel } from "utils/sentryLogger";
import { sampleOutputLevel } from "utils/audioLevel";

// With webAudioMix every remote voice plays through LiveKit's shared
// AudioContext. When iPadOS takes the audio away (Bluetooth headset profile
// flip, Siri, a phone call) WebKit parks that context in its non-standard
// "interrupted" state and fires no element event; LiveKit only ever resumes
// "suspended", so the voices stay silent while everything reports healthy.
//
// Two Safari signals see it: navigator.audioSession (iOS 17+) reports the OS
// interruption itself, and the context's own statechange reports the parked
// context. Both are logged, and the context is resumed as soon as the session
// is back, or on the next gesture if Safari wants one. Declaring the session
// "play-and-record" also tells iOS this page is a call, so it keeps the
// headset on the call route instead of re-guessing at every mic change.
export default function useAudioSessionRecovery() {
  const { room } = useVideoContext();

  useEffect(() => {
    const session = navigator.audioSession;
    if (session) {
      try {
        session.type = "play-and-record";
      } catch (e) {
        logLiveKit("audioSession.type rejected", {
          level: LogLevel.WARNING,
          event: "audio_session_type_failed",
          errorName: e?.name,
          errorMessage: e?.message,
        });
      }
    }

    // LiveKit types the field private; in JS it is a plain property.
    const context = () => room.audioContext;

    let disarmGesture = null;
    let failureLogged = false;
    let disposed = false;
    const disarm = () => {
      disarmGesture?.();
      disarmGesture = null;
      failureLogged = false;
    };
    const armGesture = () => {
      if (disarmGesture) return;
      const onGesture = () => resume(true);
      document.addEventListener("pointerdown", onGesture, true);
      document.addEventListener("keydown", onGesture, true);
      disarmGesture = () => {
        document.removeEventListener("pointerdown", onGesture, true);
        document.removeEventListener("keydown", onGesture, true);
      };
    };

    const resume = (viaGesture) => {
      const ctx = context();
      if (!ctx || ctx.state === "running" || ctx.state === "closed") {
        disarm();
        return;
      }
      ctx.resume().then(
        () => {
          // resume() can settle while WebKit still holds the interruption.
          if (ctx.state !== "running") {
            armGesture();
            return;
          }
          logLiveKit("Audio context resumed", {
            event: "audio_context_resumed",
            viaGesture,
          });
          disarm();
        },
        (e) => {
          if (!failureLogged) {
            failureLogged = true;
            logLiveKit("Audio context resume failed", {
              level: LogLevel.WARNING,
              event: "audio_context_resume_failed",
              viaGesture,
              errorName: e?.name,
              errorMessage: e?.message,
            });
          }
          armGesture();
        }
      );
    };

    const onSessionState = () => {
      const interrupted = session.state === "interrupted";
      logLiveKit(`Audio session ${session.state}`, {
        level: interrupted ? LogLevel.WARNING : LogLevel.INFO,
        event: "audio_session_state",
        state: session.state,
        contextState: context()?.state ?? null,
      });
      if (interrupted) armGesture();
      else if (session.state === "active") resume(false);
    };
    session?.addEventListener("statechange", onSessionState);

    // The context only exists once the room connects.
    let watched = null;
    const onContextState = () => {
      const state = watched?.state;
      logLiveKit(`Audio context ${state}`, {
        level: state === "running" ? LogLevel.INFO : LogLevel.WARNING,
        event: "audio_context_state",
        state,
        sessionState: session?.state ?? null,
      });
      if (state !== "running") resume(false);
    };
    const watchContext = () => {
      const ctx = context();
      if (!ctx || ctx === watched) return;
      watched?.removeEventListener("statechange", onContextState);
      watched = ctx;
      ctx.addEventListener("statechange", onContextState);
    };
    // Pulling the headphones out moves the output under a live session.
    // LiveKit notices and re-pins the mix context's sink, but it fires
    // audioContext.setSinkId without awaiting it and never resumes the
    // context, so Chrome leaves it parked on the device that went away and
    // every remote voice goes silent while the room still reports healthy.
    // Re-selecting in the tech check only repeats the same unresumed
    // setSinkId, which is why nothing in the UI brings the audio back — and
    // the tech check's own tone is a standalone element, so it keeps playing
    // and the check passes against a session nobody can hear.
    const pausedElements = () => {
      let paused = 0;
      room.remoteParticipants?.forEach((participant) => {
        participant.audioTrackPublications?.forEach((publication) => {
          publication.track?.attachedElements?.forEach((el) => {
            if (el.paused) paused += 1;
          });
        });
      });
      return paused;
    };

    const onActiveDeviceChanged = (kind, deviceId) => {
      if (kind !== "audiooutput") return;
      const ctx = context();
      const paused = pausedElements();
      logLiveKit("Audio output repinned", {
        event: "audio_output_repinned",
        toSinkId: deviceId,
        contextState: ctx?.state ?? null,
        pausedElements: paused,
      });
      watchContext();
      if (ctx && ctx.state !== "running") resume(false);
      else if (paused > 0) room.startAudio?.()?.catch?.(() => {});

      // Logged separately, and after the repair, so this one answers a
      // different question: did sound actually come back? Kept off the
      // synchronous event above because it takes a sampling window.
      sampleOutputLevel(room).then((reading) => {
        if (disposed) return;
        logLiveKit("Audio output level", {
          event: "audio_output_level",
          trigger: "repin",
          toSinkId: deviceId,
          ...reading,
        });
      }, () => {});
    };

    watchContext();
    room.on(RoomEvent.Connected, watchContext);
    room.on(RoomEvent.ActiveDeviceChanged, onActiveDeviceChanged);

    return () => {
      disposed = true;
      disarm();
      session?.removeEventListener("statechange", onSessionState);
      watched?.removeEventListener("statechange", onContextState);
      room.off(RoomEvent.Connected, watchContext);
      room.off(RoomEvent.ActiveDeviceChanged, onActiveDeviceChanged);
    };
  }, [room]);
}
